import React, { useState } from 'react';
import { ArrowRight, Sparkles, Zap, Loader2 } from 'lucide-react';
import { heroStats } from '../mock';
import { adminApi, formatApiErrorDetail } from '../lib/adminApi';

// Stylized SVG heatmap mock — built from scratch, not copied
const HeatmapMock = () => {
  // 7x7 grid of ranks
  const ranks = [
    [1,2,1,3,2,1,4],
    [2,1,3,1,2,4,5],
    [1,3,2,1,3,2,6],
    [3,2,1,1,4,5,8],
    [4,5,3,2,3,7,10],
    [6,7,5,4,6,9,12],
    [8,10,7,6,9,12,15],
  ];
  const colorFor = (r) => {
    if (r <= 3) return { bg: '#22C55E', fg: 'white' };
    if (r <= 10) return { bg: '#F59E0B', fg: '#1c1917' };
    return { bg: '#EF4444', fg: 'white' };
  };
  return (
    <div className="relative aspect-square w-full rounded-3xl bg-white p-4 shadow-2xl border border-stone-200 overflow-hidden">
      {/* Faux browser bar */}
      <div className="flex items-center gap-1.5 mb-3">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
        <span className="ml-2 text-[11px] text-stone-400 font-mono">ambanji.io / heatmap</span>
      </div>
      {/* Faux map background */}
      <div className="relative rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #E7F5E1 0%, #F0F9E8 40%, #E0E7FF 100%)' }}>
        {/* faint street lines */}
        <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 200 200" preserveAspectRatio="none">
          <path d="M0 40 L200 55" stroke="#9CA3AF" strokeWidth="0.6" fill="none" />
          <path d="M0 100 L200 110" stroke="#9CA3AF" strokeWidth="1" fill="none" />
          <path d="M0 160 L200 155" stroke="#9CA3AF" strokeWidth="0.6" fill="none" />
          <path d="M55 0 L45 200" stroke="#9CA3AF" strokeWidth="0.6" fill="none" />
          <path d="M120 0 L130 200" stroke="#9CA3AF" strokeWidth="1" fill="none" />
        </svg>
        <div className="relative grid grid-cols-7 gap-2 p-4">
          {ranks.flat().map((r, i) => {
            const c = colorFor(r);
            return (
              <div
                key={i}
                className="aspect-square rounded-full flex items-center justify-center text-[11px] font-bold shadow-sm"
                style={{ backgroundColor: c.bg, color: c.fg, animationDelay: `${i * 30}ms` }}
              >
                {r}
              </div>
            );
          })}
        </div>
      </div>
      {/* Legend */}
      <div className="mt-3 flex items-center justify-between text-[11px] text-stone-600">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>Top 3</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>4–10</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>11+</span>
        </div>
        <span className="font-mono">7 x 7 grid</span>
      </div>
    </div>
  );
};

const Hero = () => {
  const [form, setForm] = useState({ business_name: '', email: '', keyword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.email || !form.keyword) return;
    setError('');
    setLoading(true);
    try {
      await adminApi.post('/leads', form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Something went wrong, please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 dot-grid-bg opacity-60"></div>
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#F97316] opacity-10 blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#4F46E5] opacity-10 blur-3xl"></div>

      <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        {/* LEFT */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-stone-200 shadow-sm text-[12px] font-semibold text-stone-700 mb-6">
            <Sparkles size={13} className="text-[#F97316]" />
            AI teammate for your Google Business Profile
          </div>

          <h1 className="text-[42px] md:text-[54px] lg:text-[62px] font-extrabold text-stone-900 leading-[1.02]">
            Rank higher on Google Maps.<br/>
            <span className="text-[#F97316]">Without the SEO homework.</span>
          </h1>

          <p className="mt-6 text-[17px] text-stone-600 leading-relaxed max-w-xl">
            Ambanji watches your Google Business Profile 24/7, drafts posts and review replies in your voice, and hands everything off to your CRM through Ambanji Relay for one-click publishing.
          </p>

          {submitted ? (
            <div data-testid="hero-heatmap-success" className="mt-8 max-w-lg bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <p className="text-[15px] font-bold text-emerald-800">You're on the list, {form.business_name}!</p>
              <p className="text-[13px] text-emerald-700 mt-1">We're building your real Google Maps heatmap by hand — check your inbox within a day or two.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 max-w-lg space-y-3">
              <input
                data-testid="hero-heatmap-business-input"
                type="text"
                required
                value={form.business_name}
                onChange={update('business_name')}
                placeholder="Your business name"
                className="w-full px-5 py-4 rounded-xl border border-stone-300 bg-white text-[15px] focus:outline-none focus:ring-4 focus:ring-[#F97316]/20 focus:border-[#F97316] transition"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="hero-heatmap-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update('email')}
                  placeholder="Email address"
                  className="flex-1 px-5 py-4 rounded-xl border border-stone-300 bg-white text-[15px] focus:outline-none focus:ring-4 focus:ring-[#F97316]/20 focus:border-[#F97316] transition"
                />
                <input
                  data-testid="hero-heatmap-keyword-input"
                  type="text"
                  required
                  value={form.keyword}
                  onChange={update('keyword')}
                  placeholder="e.g. plumber near me"
                  className="flex-1 px-5 py-4 rounded-xl border border-stone-300 bg-white text-[15px] focus:outline-none focus:ring-4 focus:ring-[#F97316]/20 focus:border-[#F97316] transition"
                />
              </div>
              {error && (
                <p data-testid="hero-heatmap-error" className="text-[13px] text-red-600">{error}</p>
              )}
              <button
                data-testid="hero-heatmap-submit-button"
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-4 text-[14px] font-bold text-white bg-[#4F46E5] rounded-xl hover:bg-[#4338CA] hover:shadow-lg hover:-translate-y-0.5 transition inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Get free heatmap <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          <p className="mt-3 text-[13px] text-stone-500 flex items-center gap-1.5">
            <Zap size={13} className="text-[#F97316]" />
            Real Google Maps ranking data. No credit card.
          </p>
          <p className="mt-1.5 text-[12px] text-stone-500">
            🔒 We'll never share your info or spam your inbox.
          </p>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
            {heroStats.map((s) => (
              <div key={s.label} className="bg-white rounded-xl px-4 py-4 border border-stone-200">
                <p className="text-[22px] font-extrabold text-[#F97316] leading-none">{s.value}</p>
                <p className="text-[11px] text-stone-600 mt-2 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — heatmap mock */}
        <div className="relative animate-float">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#F97316]/20 to-[#4F46E5]/20 rounded-[32px] blur-2xl"></div>
          <div className="relative">
            <HeatmapMock />
            {/* Floating stat card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <ArrowRight size={18} className="text-emerald-600 -rotate-45" />
              </div>
              <div>
                <p className="text-[11px] text-stone-500 uppercase tracking-wider">Avg position</p>
                <p className="text-[16px] font-bold text-stone-900">14.3 &rarr; 4.1</p>
              </div>
            </div>
            {/* Floating pill */}
            <div className="absolute -top-4 -right-4 bg-[#4F46E5] text-white text-[12px] font-bold px-3.5 py-2 rounded-full shadow-lg">
              Weekly refresh
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
